import { Injectable } from '@angular/core'
import { Store } from '@ngrx/store'
import {
    AppState,
    createField,
    deleteField,
    initSchemas,
    selectAllSchemas,
    selectConfigSchema,
    selectSchemaEntities,
    selectSelectedTableSchemaGuid,
    selectSelectedTableSchema,
    selectSelectedTableSchemaFieldEntities,
    selectTableSchemas,
    updateField,
    updateSchemas,
    setSelectedTableSchemaGuid,
} from '../../@ngrx'
import { map } from 'rxjs/operators'
import { Dictionary, Update } from '@ngrx/entity'
import { Field, FieldEntities, FieldInsertionOptions, Folder, Schema } from '../../models'
import { getBoolean } from '../../global-util'

@Injectable({
    providedIn: 'root',
})
export class SchemaFacadeService {
    constructor(private store: Store<AppState>) {}

    selectSchemaEntities$ = this.store.select(selectSchemaEntities)

    selectAllSchemas$ = this.store.select(selectAllSchemas)

    selectTableSchemas$ = this.store.select(selectTableSchemas)

    selectSelectedTableSchemaGuid$ = this.store.select(selectSelectedTableSchemaGuid)

    selectSelectedTableSchema$ = this.store.select(selectSelectedTableSchema)

    selectSelectedTableSchemaFieldEntities$ = this.store.select(
        selectSelectedTableSchemaFieldEntities,
    )

    selectConfigSchema$ = this.store.select(selectConfigSchema)

    selectSchemaByGuid$ = (guid: string) => {
        return this.selectSchemaEntities$.pipe(
            map((schemaDictionary: Dictionary<Schema>) => schemaDictionary[guid]),
        )
    }

    selectSchemaFieldsByFolder$ = (schemaGuid: string, folder: Folder) => {
        return this.selectSchemaByGuid$(schemaGuid).pipe(
            map((schema) => {
                if (!schema) return

                return this.fieldsSortedByFolder(schema, folder)
            }),
        )
    }

    selectSchemaBySystemObjectTypeCode$ = (type: string) => {
        return this.selectAllSchemas$.pipe(
            map((schemas: Schema[]) =>
                schemas.find((schema) => schema.system_object_type_code === type),
            ),
        )
    }

    selectSelectedTableSchemaFieldEntitiesFiltered$ = (folder: Folder) => {
        return this.selectSelectedTableSchema$.pipe(
            map((schema: Schema | undefined) => {
                if (!schema) return undefined

                return this.fieldsSortedByFolder(schema, folder)
            }),
        )
    }

    initSchemas(schemas: Schema[]) {
        this.store.dispatch(initSchemas({ schemas }))
    }

    updateSchemas(schemas: Update<Schema>[]) {
        this.store.dispatch(updateSchemas({ schemas }))
    }

    updateField(field: Field) {
        this.store.dispatch(updateField({ field }))
    }

    createField(field: Field, options?: FieldInsertionOptions) {
        this.store.dispatch(createField({ field, options }))
    }

    deleteField(guid: string) {
        this.store.dispatch(deleteField({ guid }))
    }

    setSelectedTableSchemaGuid(selectedSchemaGuid: string | null) {
        this.store.dispatch(setSelectedTableSchemaGuid({ selectedSchemaGuid }))
    }

    private fieldsSortedByFolder(schema: Schema, folder: Folder) {
        return Object.keys(schema.fieldEntities)
            .filter((guid) => {
                if (getBoolean(folder.is_global.value)) return true
                return this.fieldBelongsToFolder(schema, folder, guid)
            })
            .reduce(
                (sortedFields, guid) => ({
                    ...sortedFields,
                    [guid]: schema.fieldEntities[guid],
                }),
                {} as FieldEntities,
            )
    }

    private fieldBelongsToFolder(schema: Schema, folder: Folder, guid: string) {
        return (
            schema.fieldEntities[guid].folder_guid === folder.guid ||
            schema.fieldEntities[guid].shared_with_folder?.includes(folder.guid) ||
            schema.fieldEntities[guid].folder_name?.is_global
        )
    }
}
